import React, { useState, useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { toast } from 'react-hot-toast';

import {
  fetchBotSteps,
  initializeStepStream,
  streamStepChat,
  callFunction,
  createSession,
  saveStepChat,
} from '../../services/api';
import { DataState } from './../../context/DataProvider';
import TextFormat from './../../components/TextFormat';
import { ToggleButtonCustom } from './../custom/ToggleButton';
import Button from '../custom/Button';
import Loader from '../custom/Loader';
import Loader2 from '../custom/Loader2';

const BotStepsModal = ({ botId, onClose }) => {
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chats, setChats] = useState({});
  const [currentChat, setCurrentChat] = useState({});
  const [streamingResponse, setStreamingResponse] = useState({});
  const [sessionId, setSessionId] = useState(null);
  const { userGoogleId, currentTaskId } = DataState();
  const [currentStepId, setCurrentStepId] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const isAutomatic = true;
  const [isAutomaticSwitching, setIsAutomaticSwitching] = useState(true);
  const [haveStepResults, setHaveStepResults] = useState(false);
  const isTest = true;
  const [stepCompleted, setStepCompleted] = useState(false);
  const [savedStepFinalResponse, setSavedStepFinalResponse] = useState(false);
  const [stepsFinalResponse, setStepsFinalResponse] = useState({});
  const [currentToolResults, setCurrentToolResults] = useState({});
  const [startExecution, setStartExecution] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [executingStep, setExecutingStep] = useState(null);
  const { userInfo } = DataState();

  useEffect(() => {
    if (startExecution) {
      setHaveStepResults(true);
    }
    // eslint-disable-next-line
  }, [startExecution]);

  useEffect(() => {
    if (haveStepResults && isAutomatic) {
      handleStreamStep();
      setHaveStepResults(false); // Reset the state
    }
    // eslint-disable-next-line
  }, [haveStepResults]);

  useEffect(() => {
    if (
      stepCompleted &&
      currentStepIndex < steps.length - 1 &&
      isAutomaticSwitching
    ) {
      setStepCompleted(false);
      setCurrentStepId(steps[currentStepIndex + 1].id);
      setCurrentStepIndex(currentStepIndex + 1);
      setHaveStepResults(true);
    }
    // eslint-disable-next-line
  }, [stepCompleted]);

  useEffect(() => {
    if (botId) {
      const fetchSteps = async () => {
        try {
          setLoading(true);
          const stepsData = await fetchBotSteps(
            botId,
            userGoogleId,
            currentTaskId,
          );
          setSteps(stepsData.steps);

          const initialStreamingResponse = {};

          // Initialize chats for each step with the step prompt
          const initialChats = {};
          const chats_temp = {};
          stepsData.steps.forEach((step) => {
            initialChats[step.step_id] = {
              role: 'user',
              content: step.step_prompt,
            };
            initialStreamingResponse[step.step_id] = {
              role: 'assistant',
              content: '',
              tool_calls: [],
            };
            chats_temp[step.step_id] = [];
          });
          setCurrentChat(initialChats);
          setCurrentStepId(stepsData.steps[0].id);
          setCurrentStepIndex(0);
          setChats(chats_temp);
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };

      fetchSteps();
    }
    // eslint-disable-next-line
  }, [botId]);

  const handleCreateSession = async () => {
    setError(null);
    try {
      const response = await createSession(
        {
          bot_id: botId,
          task_id: currentTaskId,
        },
        userGoogleId,
      );
      if (response.error) {
        setError(response.error);
        return;
      }
      setSessionId(response.session_id);
      // Handle success - you might want to show a success message or update the UI
    } catch (error) {
      setError(error?.message || String(error));
    }
  };

  const handleStreamStep = async (step_execution_id) => {
    try {
      if (step_execution_id) {
        setCurrentStepId(step_execution_id);
      }
      if (
        !userGoogleId ||
        !sessionId ||
        !(currentStepId || step_execution_id)
      ) {
        setError('Session Authentication Error');
        return;
      }

      const stepId = step_execution_id ? step_execution_id : currentStepId;

      if (
        !currentChat[stepId].content &&
        (!currentToolResults[stepId] || !currentToolResults[stepId].length)
      ) {
        return;
      }
      let newCurrentChat = [currentChat[stepId]];
      if (currentToolResults[stepId] && currentToolResults[stepId].length) {
        newCurrentChat = currentToolResults[stepId];
      }
      const stepChats = [...chats[stepId], ...newCurrentChat];

      const data = {
        session_id: sessionId,
        step_id: stepId,
        chats: stepChats,
        bot_id: botId,
        isTest,
      };
      setExecutingStep(stepId);
      const updatedStepChats = [...chats[stepId]]; // Local variable to hold the updated conversations

      // Initialize the step stream
      let delay = 1000;
      let attempt = 0;
      const maxRetries = 5;

      const retryStream = async () => {
        try {
          // Stream the step chat
          const streamResponse = await initializeStepStream(data, userGoogleId);
          const streamId = streamResponse.stream_id;
          const eventSource = streamStepChat(streamId, userGoogleId);

          // Handle the streaming response
          setCurrentToolResults({ ...currentToolResults, [stepId]: [] });
          setStreamingResponse({
            ...streamingResponse,
            [stepId]: { role: 'assistant', content: '', tool_calls: [] },
          });
          let toolCalls = {}; // To store tool calls by index (0, 1, etc.)

          // Construct the final response message
          let currentStreamingResponseMessage = {
            role: 'assistant',
            tool_calls: [],
            content: '',
          };

          eventSource.onmessage = async (event) => {
            const chunk = JSON.parse(event.data);
            for (const choice of chunk.choices) {
              const content = choice.delta?.content || '';
              if (choice.finish_reason) {
                eventSource.close();
                // setIsBusy(false);
                setCurrentChat({
                  ...currentChat,
                  [stepId]: { role: 'user', content: '' },
                });

                for (const chat of newCurrentChat) {
                  await saveStepChat(
                    botId,
                    stepId,
                    userGoogleId,
                    sessionId,
                    chat,
                  );
                }
                await saveStepChat(
                  botId,
                  stepId,
                  userGoogleId,
                  sessionId,
                  currentStreamingResponseMessage,
                );

                if (
                  savedStepFinalResponse &&
                  currentStreamingResponseMessage.content &&
                  !currentStreamingResponseMessage.tool_calls.length
                ) {
                  setChats({
                    ...chats,
                    [stepId]: [
                      ...updatedStepChats,
                      ...newCurrentChat,
                      currentStreamingResponseMessage,
                      {
                        role: 'bot',
                        type: 'step_response',
                        content: stepsFinalResponse[stepId],
                      },
                    ],
                  });
                  await saveStepChat(botId, stepId, userGoogleId, sessionId, {
                    role: 'bot',
                    content: stepsFinalResponse[stepId],
                    type: 'step_response',
                  });
                  if (currentStreamingResponseMessage.content) {
                    setSavedStepFinalResponse(false);
                    setStepCompleted(true);
                    setExecutingStep(null);
                    setLoading(false);
                  }
                } else {
                  setChats({
                    ...chats,
                    [stepId]: [
                      ...updatedStepChats,
                      ...newCurrentChat,
                      currentStreamingResponseMessage,
                    ],
                  });
                }

                setStreamingResponse({
                  ...streamingResponse,
                  [stepId]: { role: 'assistant', content: '', tool_calls: [] },
                });
                setError(null);
              } else if (content) {
                currentStreamingResponseMessage.content += content;
                setStreamingResponse({
                  ...streamingResponse,
                  [stepId]: currentStreamingResponseMessage,
                });
              } else if (choice.delta?.tool_calls) {
                choice.delta.tool_calls.forEach((toolCall) => {
                  const index = toolCall.index;
                  if (index !== undefined) {
                    if (!toolCalls[index]) {
                      // Initialize the tool call details if not already present
                      currentStreamingResponseMessage.tool_calls.push({
                        id: toolCall.id || '',
                        function: {
                          arguments: '',
                          name: '', // Initialize an empty name
                        },
                        type: 'function',
                      });
                      setStreamingResponse({
                        ...streamingResponse,
                        [stepId]: currentStreamingResponseMessage,
                      });
                      toolCalls[index] = {
                        id: toolCall.id || '',
                      };
                    }

                    // Append the arguments as they come in chunks
                    if (toolCall.function && toolCall.function.arguments) {
                      currentStreamingResponseMessage.tool_calls[
                        index
                      ].function.arguments += toolCall.function.arguments;
                      setStreamingResponse({
                        ...streamingResponse,
                        [stepId]: currentStreamingResponseMessage,
                      });
                    }

                    // Handle the function name if it's present in this chunk
                    if (toolCall.function && toolCall.function.name) {
                      currentStreamingResponseMessage.tool_calls[
                        index
                      ].function.name = toolCall.function.name;
                      setStreamingResponse({
                        ...streamingResponse,
                        [stepId]: currentStreamingResponseMessage,
                      });
                    }
                  }
                });
              }
            }
          };

          eventSource.onerror = async (err) => {
            setExecutingStep(null);
            eventSource.close();
            if (attempt < maxRetries) {
              attempt += 1;
              delay = delay * 2; // Exponential backoff
              await new Promise((resolve) => setTimeout(resolve, delay));
              retryStream(); // Retry the stream
            } else {
              setError('Max retries reached. Please try again later.');
            }
          };

          eventSource.onclose = () => {
            setExecutingStep(null);
            this.setState({ loading: false });
          };
        } catch (err) {
          setExecutingStep(null);
          setError(JSON.stringify(err));
        }
      };

      retryStream();
    } catch (err) {
      setExecutingStep(null);
      setError(JSON.stringify(err));
    }
  };

  useEffect(() => {
    const handleAutomaticToolUse = async () => {
      if (isAutomatic && currentStepId) {
        // Iterate through all the chats for each step
        const stepChats = chats[currentStepId];

        if (!stepChats.length) {
          return;
        }

        const lastChat = stepChats[stepChats.length - 1];
        if (lastChat && lastChat.tool_calls && lastChat.tool_calls.length > 0) {
          let allHandled = true;
          let tool_responses = [];
          for (const tool_call of lastChat.tool_calls) {
            allHandled = false; // Set to false if there is at least one unhandled tool_use
            const data = await handleFunctionCall(tool_call, currentStepId);
            if (!data) {
              return;
            }
            tool_responses.push(data);
          }
          if (!allHandled) {
            setCurrentToolResults({
              ...currentToolResults,
              [currentStepId]: tool_responses,
            });

            setHaveStepResults(true); // Only set hasTaskResults to true when all tool_uses are handled
          }
        }
      }
    };

    handleAutomaticToolUse();
    // eslint-disable-next-line
  }, [chats, isAutomatic]);

  const handleFunctionCall = async (tool, stepId) => {
    try {
      let tempToolDict = {};
      if (tool.function.name === 'save_final_response') {
        tempToolDict['final_response_markdown'] = JSON.parse(
          tool.function.arguments,
        ).final_response_markdown;
      } else if (tool.function.name === 'save_user_feedback') {
        tempToolDict['feedback'] = JSON.parse(tool.function.arguments).feedback;
      } else if (tool.function.name === 'log_error') {
        tempToolDict['error_message'] = JSON.parse(
          tool.function.arguments,
        ).error_message;
      }
      const data = await callFunction(
        {
          id: tool.id,
          name: tool.function.name,
          arguments: tool.function.arguments,
          type: tool.type,
          task_id: currentTaskId,
          isTest,
          bot_id: botId,
          is_bot: true,
        },
        userGoogleId,
      );

      if (data.error) {
        setError(data.error);
        return false;
      } else {
        if (!data.is_error && tempToolDict['final_response_markdown']) {
          setSavedStepFinalResponse(true);
          setStepsFinalResponse({
            ...stepsFinalResponse,
            [stepId]: tempToolDict['final_response_markdown'],
          });
        }
        setError(null);
        return data;
      }
    } catch (error) {
      toast.error(error);
      setError('An error occurred while calling the function.');
      return false;
    } finally {
    }
  };

  const handleToggle = () => {
    setIsAutomaticSwitching(!isAutomaticSwitching);
  };

  useEffect(() => {
    handleCreateSession();
    // eslint-disable-next-line
  }, []);

  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const selectedStepId = null;

  const renderStepDetails = (step) => {
    const stepChats = [
      ...(chats[step.step_id] ? chats[step.step_id] : []),
      ...(streamingResponse[step.step_id]
        ? [currentChat[step.step_id], streamingResponse[step.step_id]]
        : []),
    ];

    return (
      <div className="border-t border-zinc-700 p-4">
        <div className="mt-2">
          {stepChats.map((chat, i) => {
            const { role, content } = chat;
            const textContent = content;

            if (!textContent || !textContent.trim()) {
              return null;
            }

            return role === 'bot' ? (
              <pre
                key={i}
                className="overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-zinc-800 p-4 text-gray-100"
              >
                <TextFormat response={textContent} />
              </pre>
            ) : (
              <div key={i} className="flex items-start gap-1">
                <div className="min-h-10 min-w-10 pt-3">
                  {role === 'user' ? (
                    <img
                      src={userInfo ? userInfo.picture : '../assets/nirmal.png'}
                      alt="User"
                      className="h-7 w-7 rounded-full object-cover"
                    />
                  ) : (
                    <img
                      src="../assets/logo.png"
                      alt="Bot"
                      className="h-7 w-7 rounded-full bg-black object-contain p-1"
                    />
                  )}
                </div>
                <p
                  className={`text-sm ${
                    role === 'user' ? 'text-zinc-200' : 'text-zinc-300'
                  }`}
                >
                  <TextFormat response={textContent} />
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  // Toggle step expansion
  const toggleStepExpansion = (stepId) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepId]: !prev[stepId],
    }));
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-50"></div>
      <div className="z-10 flex h-[90%] w-[90%] max-w-3xl flex-col rounded-lg bg-zinc-900">
        <div className="flex justify-between gap-3 border-b border-zinc-800 px-3 py-5">
          <h1 className="px-3 py-1.5 text-xl font-bold">Test Bot</h1>
          <div>
            <button
              onClick={onClose}
              className="rounded-lg bg-zinc-800 p-1.5 transition-all duration-200 hover:bg-zinc-700"
            >
              <IoMdClose />
            </button>
          </div>
        </div>
        <div className="flex-grow overflow-y-auto">
          {loading ? (
            <div className="flex min-h-96 items-center justify-center text-zinc-400">
              <span className="h-12">
                <Loader />
              </span>
            </div>
          ) : error ? (
            <p className="text-center text-red-500">Error: {error}</p>
          ) : steps.length > 0 ? (
            <ul className="space-y-4 p-6">
              {steps.map((step) => (
                <div
                  key={step.step_id}
                  className="overflow-hidden rounded-lg border border-zinc-700 bg-zinc-800"
                >
                  {/* eslint-disable-next-line */}
                  <div
                    className="flex cursor-pointer items-center justify-between p-4 transition hover:bg-zinc-700"
                    onClick={() => toggleStepExpansion(step.step_id)}
                  >
                    <div>
                      <strong className="text-zinc-200">
                        Step {step.step_index + 1}: {step.step_title}
                      </strong>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center space-x-2">
                        <MdKeyboardArrowDown />
                        {executingStep === step.step_id && <Loader2 />}
                      </div>
                      <button
                        onClick={() => handleStreamStep(step.step_id)}
                        className={`flex w-fit items-center gap-2 rounded-md px-2 py-1 transition duration-150
                      ${
                        !sessionId || !userGoogleId
                          ? 'cursor-not-allowed'
                          : 'bg-zinc-900 text-white hover:bg-zinc-900/80'
                      }`}
                        disabled={!sessionId || !userGoogleId}
                      >
                        {executingStep === step.step_id
                          ? 'Executing'
                          : 'Execute'}
                      </button>
                    </div>
                  </div>

                  {expandedSteps[step.step_id] && renderStepDetails(step)}
                </div>
              ))}
            </ul>
          ) : (
            <p className="p-6 text-center text-zinc-400">No steps available</p>
          )}
        </div>
        <div className="border-t border-zinc-800 p-6">
          <div className="flex items-center justify-end gap-5">
            <div className="flex items-center gap-2">
              <ToggleButtonCustom
                isChecked={isAutomaticSwitching}
                onChange={handleToggle}
              />
              <span className="m-1 text-sm text-white">
                Automatic Step Switching
              </span>
            </div>
            <Button
              onClick={() => {
                setStartExecution(true);
              }}
              loading={!sessionId || startExecution}
            >
              Start Execution
            </Button>
          </div>
        </div>
      </div>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        stepId={selectedStepId}
      />
    </div>
  );
};

export default BotStepsModal;

const FeedbackModal = ({ isOpen, onClose, stepId }) => {
  const [input, setInput] = useState('');

  const handleSubmit = () => {
    setInput('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* eslint-disable-next-line */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />
      <div className="z-10 flex w-96 flex-col rounded-lg bg-zinc-900">
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h2 className="text-lg font-semibold">Add Feedback</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 transition-colors hover:bg-zinc-800"
          >
            <IoMdClose className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Enter your feedback..."
            className="min-h-32 w-full rounded-md border border-zinc-700 bg-zinc-800 p-3 focus:border-zinc-600 focus:outline-none"
          />
        </div>
        <div className="flex justify-end border-t border-zinc-800 p-4">
          <button
            onClick={handleSubmit}
            className="rounded-md bg-zinc-800 px-4 py-2 text-white transition duration-150 hover:bg-zinc-700"
          >
            Send Feedback
          </button>
        </div>
      </div>
    </div>
  );
};
